import { clearLoginHandoffToken } from './useLoginHandoff'
import { api } from "boot/axios"
import { LocalStorage } from 'quasar'
import useNotifications from "./useNotifications.js"
import { setGlobalTenant } from "src/stores/tenant.js"
import { resetFeatureGate } from "./useFeatureGate.js"

export default () => {
	const { notifyError } = useNotifications()

	// This returns whether or not the user is currently authenticated (via cookie).
	async function isAuthenticated() {
		if (!api.defaults.headers.common['Authorization']) {
			let token = LocalStorage.getItem("api_token")
			api.defaults.headers.common['Authorization'] = "Bearer " + token
			console.log("isAuthenticated: Loaded token from session storage.")
		}

		console.log("isAuthenticated: Attempting to load the user's profile.")
		try {
			let profile = null
			let permissions = []
			let customer = null

			let loggedUserPromise = api.get('/v1/logged-user', { cache: false, redirect: false })
			let permissionsPromise = api.get('/v1/user/self/permission', { cache: false, redirect: false })
			let customerPromise = api.get('/v1/customer/self', { cache: false, redirect: false })

			let values = await Promise.all([loggedUserPromise, permissionsPromise, customerPromise])
			if (values[0].data) {
				profile = values[0].data || {}
			}
			if (values[1].data) {
				permissions = values[1].data || []
			}
			if (values[2].data) {
				customer = values[2].data || {}
				if (customer.name != "") {
					document.title = customer.name
				}
			}

			try {
				LocalStorage.set("user_profile", profile)
				LocalStorage.set("user_permissions", permissions)
				LocalStorage.set("customer", customer)
			} catch (e) {
				notifyError("Error saving the user's profile to local storage: " + e)
				return false
			}
			return true
		} catch (error) {
			console.log("isAuthenticated: Caught an error checking authentication:", error)
			LocalStorage.remove("user_profile")
			LocalStorage.remove("user_permissions")
			LocalStorage.remove("customer")
			return false
		}
	}

	// This attempts to log the user in with a username and password.
	//
	// This returns the token claims from the resulting token.
	//
	// This will throw an error if the login did not succeed.
	async function login(username, password) {
		let token

		console.log("login: Attempting to log in with username and password.")
		if (username !== '@') {
			delete (api.defaults.headers.common['Authorization'])

			let result = await api.post(
				"/v1/auth/session",
				{},
				{
					redirect: false,
					auth: {
						username: username,
						password: password,
					},
				}
			)
			token = result.data.token
		} else {
			token = password
		}

		return await saveTokenAndReturnClaims(token)
	}

	async function saveTokenAndReturnClaims(token) {
		let claims
		saveToken(token)

		let parts = token.split(".")
		if (parts.length <= 1) {
			claims = {}
		} else if (parts.length > 1) {
			let content = atob(parts[1])
			claims = JSON.parse(content)
		}

		// Only load the full user profile when the token is not restricted.
		// Tokens with twoFactorRequired or mfaSetupRequired are blocked by the
		// backend middleware and cannot access protected endpoints.
		// passwordChangeRequired tokens CAN access protected endpoints.
		if (!claims.twoFactorRequired && !claims.mfaSetupRequired) {
			const authenticated = await isAuthenticated()
			if (!authenticated) {
				throw new Error("Authentication failed")
			}
		}

		return claims
	}

	// This attempts to log the user in with a username and password.
	//
	// This returns the token claims from the resulting token.
	//
	// This will throw an error if the login did not succeed.
	async function loginWithMFA(code) {
		console.log("loginWithMFA: Attempting to log in with an MFA code.")
		const result = await api.post(
			"/v1/auth/mfa",
			{
				value: code,
			},
		)
		const claims = await saveTokenAndReturnClaims(result.data.token)
		console.log("loginWithMFA: claims:", claims)

		return claims
	}

	// Returns a promise from the performed ajax. Use response.then(...).catch(error) pattern
	// to handle the result.
	async function logout() {
		// Drop any unconsumed external-login handoff: it is a live session token,
		// and on a shared browser the next visit to /login would consume it and
		// sign that person in as whoever abandoned the earlier login.
		clearLoginHandoffToken()
		let response = null
		try {
			response = await api.delete("/v1/auth/session")
		} catch (error) {
			// If we request failed, that doesn't really matter.
			// The entirety of the session is local via the token, so as long as we
			// remove it here, then we have logged out.
			console.warn("logout: Could not delete session:", error)
		}

		// Reset the tenant store by setting it to an invalid tenant
		setGlobalTenant({ tenant: { id: 0 } })

		// The licence cache is module-level, so it outlives the session and
		// LocalStorage.clear() below does not touch it. Left behind, the next
		// login in this tab reads the previous user's plan until the tenant
		// picker lands.
		resetFeatureGate()

		// Reset the local storage
		LocalStorage.clear()

		delete (api.defaults.headers.common['Authorization'])

		return response
	}

	//Requests a login with Microsoft and redirects to the external page in the process was started successfully.
	async function loginWithMicrosoft() {
		try {
			const response = await api.get("/v1/integration/microsoft/login")
			if (response.status === 200) {
				const url = response.data.url
				window.location.replace(url)
			} else {
				throw new Error("Failed to login with Microsoft.")
			}
		}
		catch {
			throw new Error("Failed to login with Microsoft.")
		}
	}

	// Starts an SSO login for the given email. The backend maps the email's
	// domain to the tenant's registered identity provider and returns its
	// authorization URL; we redirect the browser there. Throws (with the
	// backend's message) when no provider serves the domain.
	async function startSSO(email) {
		const response = await api.post("/v1/auth/sso/start", { email }, { redirect: false })
		const url = response.data && response.data.url
		if (!url) {
			throw new Error("Single sign-on is not enabled for this account.")
		}
		window.location.replace(url)
	}

	// Retrieves the API keys and assigns them to the provided variable.
	// apiKeys should be reactive (ref'ed)
	function getApiKeys(apiKeys, segmentId = 0) {
		// Build the query params
		let queryParams = {
			segmentId: segmentId,
		}
		// Grab the keys
		return api.get("/v1/api-key", { params: queryParams })
			.then((response) => {
				const result = response.data
				apiKeys.value = result
			})
			.catch((error) => {
				notifyError(error)
			})
	}

	function saveToken(token) {
		console.log("saveToken: Setting bearer token.")
		api.defaults.headers.common['Authorization'] = 'Bearer ' + token
		LocalStorage.set("api_token", token)
	}

	async function mfaSetup() {
		let result = await api.post("/v1/auth/mfa-setup", {})
		return result.data
	}

	async function mfaSetupVerify(totpId, code) {
		let result = await api.post("/v1/auth/mfa-setup/verify", {
			totpId: totpId,
			value: code,
		})
		return await saveTokenAndReturnClaims(result.data.token)
	}

	return {
		getApiKeys,
		isAuthenticated,
		login,
		loginWithMFA,
		logout,
		loginWithMicrosoft,
		mfaSetup,
		mfaSetupVerify,
		saveTokenAndReturnClaims,
		startSSO,
	}
}
